import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { UserContext } from 'context';
import { Formik, Field } from 'formik';

import Box from '@material-ui/core/Box';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { TextField } from 'components/FormFields';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { MultipleVehicleGroupsSelect } from 'components/AsyncFields';
import { userGroupsActions } from 'state/modules/userGroups';
import { UserGroupsTypesSelect, UserGroupsRolesSelect } from 'components/DataFields';

import useStyles from './UserGroupsDetails.styled';

const UserGroupsDetails = ({ formRef, errors, step, exit, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    default_vehicle_group_id: Yup.array(Yup.object().shape({ id: Yup.number() })),
    role_default: Yup.string().required(i18n.t('common.required_field')),
    user_group_type: Yup.string().required(i18n.t('common.required_field')),
  });

  const initialValues = {
    name: step?.name || '',
    default_vehicle_group_id: step?.default_vehicle_group || [],
    role_default: step?.role_default || '',
    user_group_type: step?.user_group_type || '',
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
      default_vehicle_group_id: values.default_vehicle_group_id.map((vg) => vg.id),
    };

    dispatch(userGroupsActions.fetchCreateUserGroup(payload, 'driver', 'details', exit));
  };

  const vg_params = {
    group_type: ['CUSTOMER', 'OWNER'],
    limit: 20,
  };

  const getOptionLabel = (option) => {
    const groupTypeName =
      option?.group_type === 'OWNER'
        ? i18n.t('user_groups.owner_vehicle_group')
        : i18n.t('user_groups.vehicle_group');
    return groupTypeName + ' - ' + option?.name;
  };

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={11}>
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              enableReinitialize={true}
              onSubmit={onSubmit}
            >
              {({ handleSubmit }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        <Box fontWeight="fontWeightBold" fontSize={14} mb={1}>
                          {i18n.t('user_groups.general_info')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        type="text"
                        name="name"
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('common.name')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        required
                        name="user_group_type"
                        disabled={type === 'update'}
                        component={UserGroupsTypesSelect}
                        asyncErrors={errors?.user_group_type}
                        label={i18n.t('user_groups.user_group_type')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Field
                        required
                        name="role_default"
                        component={UserGroupsRolesSelect}
                        asyncErrors={errors?.role_default}
                        label={i18n.t('user_groups.default_role')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        name="default_vehicle_group_id"
                        component={MultipleVehicleGroupsSelect}
                        asyncErrors={errors?.default_vehicle_group_id}
                        customParams={vg_params}
                        defaultVehicleGroup={step?.default_vehicle_group}
                        label={i18n.t(
                          orgType === 'msOwnerOrg'
                            ? 'fleets.select_fleet'
                            : 'user_groups.select_vehicle_group'
                        )}
                        getOptionLabel={getOptionLabel}
                      />
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

UserGroupsDetails.propTypes = {
  formRef: PropTypes.any,
  errors: PropTypes.object,
  step: PropTypes.object,
  exit: PropTypes.bool,
  type: PropTypes.string,
};

export default UserGroupsDetails;
